import type { DroneAlertThresholds, DroneTelemetry } from './types';

/* ------------------------------------------------------------------ *
 * 告警等级
 * ------------------------------------------------------------------ */

export type AlertLevel = 'nominal' | 'caution' | 'warning' | 'critical';

export const ALERT_LEVELS: readonly AlertLevel[] = ['nominal', 'caution', 'warning', 'critical'];

const LEVEL_RANK: Readonly<Record<AlertLevel, number>> = {
  nominal: 0,
  caution: 1,
  warning: 2,
  critical: 3,
};

export const ALERT_LEVEL_LABELS: Readonly<Record<AlertLevel, string>> = {
  nominal: '正常',
  caution: '注意',
  warning: '警告',
  critical: '严重',
};

export function compareAlertLevel(a: AlertLevel, b: AlertLevel): number {
  return LEVEL_RANK[a] - LEVEL_RANK[b];
}

export function isAlertLevelAtLeast(level: AlertLevel, min: AlertLevel): boolean {
  return LEVEL_RANK[level] >= LEVEL_RANK[min];
}

/** 取一组等级中最严重的一个。空数组视为 nominal。 */
export function worstAlertLevel(levels: readonly AlertLevel[]): AlertLevel {
  let worst: AlertLevel = 'nominal';
  for (const level of levels) {
    if (LEVEL_RANK[level] > LEVEL_RANK[worst]) worst = level;
  }
  return worst;
}

/* ------------------------------------------------------------------ *
 * 告警项
 * ------------------------------------------------------------------ */

export interface AlertItem {
  /** 稳定标识，便于前端做去重与动画 */
  id: string;
  level: AlertLevel;
  /** 参数名，例如「电池电量」 */
  label: string;
  /** 面向操作员的明确描述 */
  message: string;
  value: number | string;
  unit: string;
  /** 触发本次判定的阈值；无阈值（如失联）时为 null */
  threshold: number | null;
}

export interface AlertEvaluation {
  /** 整体等级 = 最严重的单项等级 */
  level: AlertLevel;
  /** 按严重度降序排列 */
  items: AlertItem[];
}

interface LevelThresholds {
  caution: number;
  warning: number;
  critical: number;
}

interface MetricSpec {
  id: string;
  label: string;
  unit: string;
  /** 数值越低越危险，还是越高越危险 */
  direction: 'lower-is-worse' | 'higher-is-worse';
  pick: (thresholds: DroneAlertThresholds) => LevelThresholds;
  get: (telemetry: DroneTelemetry) => number | undefined;
  format: (value: number) => string;
  message: (value: number, level: AlertLevel) => string;
}

const round1 = (value: number): string => value.toFixed(1);
const round0 = (value: number): string => value.toFixed(0);

/** 按方向解析当前数值落入的等级。 */
function levelFor(value: number, direction: MetricSpec['direction'], t: LevelThresholds): AlertLevel {
  if (direction === 'lower-is-worse') {
    if (value <= t.critical) return 'critical';
    if (value <= t.warning) return 'warning';
    if (value <= t.caution) return 'caution';
    return 'nominal';
  }
  if (value >= t.critical) return 'critical';
  if (value >= t.warning) return 'warning';
  if (value >= t.caution) return 'caution';
  return 'nominal';
}

/** 命中等级时对应的阈值，用于在告警条里说明「因什么触发」。 */
function thresholdFor(level: AlertLevel, t: LevelThresholds): number | null {
  if (level === 'critical') return t.critical;
  if (level === 'warning') return t.warning;
  if (level === 'caution') return t.caution;
  return null;
}

/* ------------------------------------------------------------------ *
 * 指标定义
 * ------------------------------------------------------------------ */

export const METRIC_SPECS: readonly MetricSpec[] = [
  {
    id: 'battery.percent',
    label: '电池电量',
    unit: '%',
    direction: 'lower-is-worse',
    pick: (t) => ({
      caution: t.batteryPercentCaution,
      warning: t.batteryPercentWarning,
      critical: t.batteryPercentCritical,
    }),
    get: (telemetry) => telemetry.battery.percent,
    format: round0,
    message: (value, level) =>
      level === 'critical'
        ? `电量严重不足（${round0(value)}%），请立即返航或降落`
        : `电量偏低（${round0(value)}%）`,
  },
  {
    id: 'battery.cellVoltage',
    label: '单体电压',
    unit: 'V',
    direction: 'lower-is-worse',
    pick: (t) => ({
      caution: t.cellVoltageCaution,
      warning: t.cellVoltageWarning,
      critical: t.cellVoltageCritical,
    }),
    get: (telemetry) => {
      const cells = telemetry.battery.cellVoltagesV;
      if (cells.length === 0) return undefined;
      let min = cells[0] as number;
      for (const cell of cells) if (cell < min) min = cell;
      return min;
    },
    format: (value) => value.toFixed(2),
    message: (value, level) =>
      level === 'critical'
        ? `单体电压过低（${value.toFixed(2)}V），存在掉电风险`
        : `单体电压偏低（${value.toFixed(2)}V）`,
  },
  {
    id: 'battery.temperature',
    label: '电池温度',
    unit: '°C',
    direction: 'higher-is-worse',
    pick: (t) => ({
      caution: t.batteryTempCaution,
      warning: t.batteryTempWarning,
      critical: t.batteryTempCritical,
    }),
    get: (telemetry) => telemetry.battery.temperatureC,
    format: round1,
    message: (value, level) =>
      level === 'critical'
        ? `电池温度过高（${round1(value)}°C），请立即降低负载并降落`
        : `电池温度偏高（${round1(value)}°C）`,
  },
  {
    id: 'gps.satellites',
    label: '可见卫星',
    unit: '颗',
    direction: 'lower-is-worse',
    pick: (t) => ({
      caution: t.satellitesCaution,
      warning: t.satellitesWarning,
      critical: t.satellitesCritical,
    }),
    get: (telemetry) => telemetry.gps.satellites,
    format: round0,
    message: (value, level) =>
      level === 'critical'
        ? `可见卫星严重不足（${round0(value)} 颗），定位不可靠`
        : `可见卫星偏少（${round0(value)} 颗）`,
  },
  {
    id: 'gps.hdop',
    label: 'HDOP',
    unit: '',
    direction: 'higher-is-worse',
    pick: (t) => ({ caution: t.hdopCaution, warning: t.hdopWarning, critical: t.hdopCritical }),
    get: (telemetry) => telemetry.gps.hdop,
    format: (value) => value.toFixed(2),
    message: (value, level) =>
      level === 'critical'
        ? `定位精度极差（HDOP ${value.toFixed(2)}）`
        : `定位精度下降（HDOP ${value.toFixed(2)}）`,
  },
  {
    id: 'opticalFlow.quality',
    label: '光流质量',
    unit: '',
    direction: 'lower-is-worse',
    pick: (t) => ({
      caution: t.opticalFlowCaution,
      warning: t.opticalFlowWarning,
      critical: t.opticalFlowCritical,
    }),
    get: (telemetry) => (telemetry.opticalFlow.enabled ? telemetry.opticalFlow.quality : undefined),
    format: round0,
    message: (value, level) =>
      level === 'critical'
        ? `光流质量严重不足（${round0(value)}），室内/无 GPS 定位不可信`
        : `光流质量下降（${round0(value)}）`,
  },
  {
    id: 'link.quality',
    label: '信号质量',
    unit: '',
    direction: 'lower-is-worse',
    pick: (t) => ({
      caution: t.linkQualityCaution,
      warning: t.linkQualityWarning,
      critical: t.linkQualityCritical,
    }),
    get: (telemetry) => telemetry.link.quality,
    format: round0,
    message: (value, level) =>
      level === 'critical'
        ? `图传/数传链路质量极差（${round0(value)}）`
        : `链路质量下降（${round0(value)}）`,
  },
  {
    id: 'link.latency',
    label: '链路延迟',
    unit: 'ms',
    direction: 'higher-is-worse',
    pick: (t) => ({
      caution: t.latencyCaution,
      warning: t.latencyWarning,
      critical: t.latencyCritical,
    }),
    get: (telemetry) => telemetry.link.latencyMs,
    format: round0,
    message: (value, level) =>
      level === 'critical'
        ? `链路延迟过高（${round0(value)}ms），实时控制不可靠`
        : `链路延迟升高（${round0(value)}ms）`,
  },
  {
    id: 'attitude.tilt',
    label: '姿态倾角',
    unit: '°',
    direction: 'higher-is-worse',
    pick: (t) => ({
      caution: t.attitudeCaution,
      warning: t.attitudeWarning,
      critical: t.attitudeCritical,
    }),
    get: (telemetry) =>
      Math.max(Math.abs(telemetry.attitude.rollDeg), Math.abs(telemetry.attitude.pitchDeg)),
    format: round1,
    message: (value, level) =>
      level === 'critical'
        ? `姿态倾角过大（${round1(value)}°），存在失稳风险`
        : `姿态倾角偏大（${round1(value)}°）`,
  },
];

/* ------------------------------------------------------------------ *
 * 非数值检查
 * ------------------------------------------------------------------ */

const FIX_TYPE_LABELS: Readonly<Record<string, string>> = {
  none: '无定位',
  '2d': '2D 定位',
  '3d': '3D 定位',
  'rtk-float': 'RTK 浮动解',
  'rtk-fixed': 'RTK 固定解',
};

export function gpsFixTypeLabel(fixType: string): string {
  return FIX_TYPE_LABELS[fixType] ?? fixType;
}

function evaluateGpsFix(telemetry: DroneTelemetry): AlertItem | null {
  const { fixType } = telemetry.gps;
  if (fixType === 'none') {
    return {
      id: 'gps.fixType',
      level: 'critical',
      label: '定位状态',
      message: '定位已丢失，飞行器仅能依赖光流/惯导，请立即评估返航',
      value: gpsFixTypeLabel(fixType),
      unit: '',
      threshold: null,
    };
  }
  if (fixType === '2d') {
    return {
      id: 'gps.fixType',
      level: 'warning',
      label: '定位状态',
      message: '当前为 2D 定位，高度信息不可信',
      value: gpsFixTypeLabel(fixType),
      unit: '',
      threshold: null,
    };
  }
  return null;
}

function evaluateMission(telemetry: DroneTelemetry): AlertItem | null {
  if (telemetry.flight.missionState !== 'failed') return null;
  return {
    id: 'flight.missionState',
    level: 'warning',
    label: '航线任务',
    message: '航线任务执行失败，请检查任务文件与飞行器状态',
    value: '执行失败',
    unit: '',
    threshold: null,
  };
}

function evaluateStale(
  telemetry: DroneTelemetry,
  staleAfterMs: number,
  now: number,
): AlertItem | null {
  const ageMs = Math.max(0, now - telemetry.receivedAt);
  if (ageMs > staleAfterMs * 3) {
    return {
      id: 'link.stale',
      level: 'critical',
      label: '链路失联',
      message: `已超过 ${(ageMs / 1000).toFixed(1)} 秒未收到遥测帧，链路判定为失联`,
      value: Number((ageMs / 1000).toFixed(1)),
      unit: 's',
      threshold: Number(((staleAfterMs * 3) / 1000).toFixed(1)),
    };
  }
  if (ageMs > staleAfterMs) {
    return {
      id: 'link.stale',
      level: 'warning',
      label: '链路延迟',
      message: `已超过 ${(ageMs / 1000).toFixed(1)} 秒未收到遥测帧，链路不稳定`,
      value: Number((ageMs / 1000).toFixed(1)),
      unit: 's',
      threshold: Number((staleAfterMs / 1000).toFixed(1)),
    };
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * 主入口
 * ------------------------------------------------------------------ */

export interface EvaluateAlertsInput {
  telemetry: DroneTelemetry | null;
  thresholds: DroneAlertThresholds;
  staleAfterMs: number;
  /** 当前时间（epoch 毫秒），由调用方注入以便测试 */
  now: number;
}

/**
 * 评估一帧遥测的告警状态。
 * 纯函数：相同输入必定得到相同输出，便于单测与在 Worker 侧复用。
 */
export function evaluateAlerts(input: EvaluateAlertsInput): AlertEvaluation {
  const { telemetry, thresholds, staleAfterMs, now } = input;

  if (!telemetry) {
    return {
      level: 'critical',
      items: [
        {
          id: 'link.noTelemetry',
          level: 'critical',
          label: '遥测缺失',
          message: '尚未收到该飞行器的任何遥测帧，请检查接入配置与网络连通性',
          value: '无数据',
          unit: '',
          threshold: null,
        },
      ],
    };
  }

  const items: AlertItem[] = [];

  for (const spec of METRIC_SPECS) {
    const value = spec.get(telemetry);
    if (value === undefined || !Number.isFinite(value)) continue;
    const limits = spec.pick(thresholds);
    const level = levelFor(value, spec.direction, limits);
    if (level === 'nominal') continue;
    items.push({
      id: spec.id,
      level,
      label: spec.label,
      message: spec.message(value, level),
      value: Number(spec.format(value)),
      unit: spec.unit,
      threshold: thresholdFor(level, limits),
    });
  }

  const extras = [
    evaluateGpsFix(telemetry),
    evaluateMission(telemetry),
    evaluateStale(telemetry, staleAfterMs, now),
  ];
  for (const extra of extras) if (extra) items.push(extra);

  items.sort((a, b) => {
    const byLevel = compareAlertLevel(b.level, a.level);
    if (byLevel !== 0) return byLevel;
    return a.id.localeCompare(b.id);
  });

  return {
    level: worstAlertLevel(items.map((item) => item.level)),
    items,
  };
}

/** 只需要整体等级时的轻量路径（总览卡片用）。 */
export function alertLevelOf(input: EvaluateAlertsInput): AlertLevel {
  return evaluateAlerts(input).level;
}
